import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import {
  EmailListItemAddress,
  EmailListItemPrio,
  EmailListItemSubject,
  EmailListItemAttach,
  EmailListItemDate,
  EmailListItemAbstractAddress,
} from "./EmailListItem";
import classnames from "classnames";
import { IndexRange } from "react-virtualized";
import { NotFoundPlaceholder } from "Components/General/NotFoundPlaceholder";
import { observer } from "mobx-react";
import { isEmpty } from "lodash";

interface EmailsTableProps extends WithTranslation {
  data: API_MailResponse[];
  selectedRowKeys: string[];
  onRowClick: (email: API_MailResponse) => void;
  onSelectRows: (id: string[], emails: API_MailResponse[]) => void;
  addressKey: "to" | "from";
  total: number;
  loadMore: (params: { offset: number; limit: number }) => Promise<void>;
}

export const EmailsTable = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<EmailsTableProps> {
      isSelectedRow = (id) => {
        const { selectedRowKeys } = this.props;
        return selectedRowKeys.some((selectedId) => selectedId === id);
      };

      handleLoadMore = ({ startIndex, stopIndex }: IndexRange) => {
        return this.props.loadMore({ offset: startIndex, limit: stopIndex - startIndex });
      };

      renderEmailAddress = (rowData: API_MailResponse, addressKey: "to" | "from") => {
        if (addressKey === "from") {
          return (
            <div>
              <EmailListItemAddress
                address={rowData[addressKey]}
                correspondents={rowData.correspondents}
                cc={rowData.cc}
                bcc={rowData.bcc}
              />
            </div>
          );
        }
        let content = (
          <EmailListItemAddress
            address={rowData[addressKey]}
            correspondents={rowData.correspondents}
            cc={rowData.cc}
            bcc={rowData.bcc}
          />
        );
        if (!isEmpty((rowData as API_MailResponse).abstractRecipients?.to)) {
          content = (
            <EmailListItemAbstractAddress
              abstractRecipients={rowData.abstractRecipients}
              correspondents={rowData.correspondents}
            />
          );
        }
        return <div>{content}</div>;
      };

      render() {
        const { t, addressKey, total } = this.props;
        const columns: TableColumnProps<API_MailResponse>[] = [
          {
            dataKey: addressKey,
            disableSort: true,
            label: t(addressKey === "to" ? "To" : "From"),
            width: 300,
            // flexShrink: 1,
            cellRenderer: ({ rowData }) => this.renderEmailAddress(rowData, addressKey),
          },
          {
            dataKey: "importance",
            disableSort: true,
            label: t("Prio"),
            width: 60,
            cellRenderer: ({ rowData }) => <EmailListItemPrio importance={rowData.importance} />,
          },
          {
            dataKey: "subject",
            disableSort: true,
            label: t("Subject"),
            width: 150,
            flexGrow: 0.94,
            cellRenderer: ({ rowData }) => (
              <div>
                <EmailListItemSubject excerpt={rowData.excerpt} subject={rowData.subject} />
              </div>
            ),
          },
          {
            dataKey: "hasAttachments",
            disableSort: true,
            label: t("Attachments"),
            width: 100,
            cellRenderer: ({ rowData }) => (
              <div>
                <EmailListItemAttach hasAttachments={rowData.hasAttachments} />
              </div>
            ),
          },
          {
            dataKey: "timestamp",
            disableSort: true,
            label: t("Date"),
            width: 170,
            cellRenderer: ({ rowData }) => <EmailListItemDate timestamp={rowData.timestamp} />,
          },
        ];
        if (!this.props.data.length) {
          return (
            <NotFoundPlaceholder
              text={t("No emails", { ns: I18N_NS.PROJECT })}
              icon="envelope"
              style={{ transform: `translate(0, 50%)` }}
            />
          );
        }

        return (
          <ScrollableTable<API_MailResponse>
            rowKey="id"
            loadMore={this.props.loadMore}
            rowCount={total}
            data={this.props.data.slice()}
            stretchHeight={true}
            useDataFromProps={true}
            columns={columns}
            onRowClick={({ rowData }) => this.props.onRowClick(rowData)}
            onSelectRows={({ selectedRowKeys, selectedRows }) =>
              this.props.onSelectRows(selectedRowKeys as string[], selectedRows)
            }
            // onSort={this.props.onSort}
            selectedRowKeys={this.props.selectedRowKeys}
            // defaultSortBy={this.props.sortBy}
            // defaultSortDirection={this.props.sortDirection}
            rowClassName={(record) =>
              classnames("email-item", {
                active: record?.unread,
                selected: this.isSelectedRow(record?.id),
              })
            }
            resizeableHeaders={"EMAIL"}
            ignoreResizeableHeaders={["id"]}
          />
        );
      }
    }
  )
);
